/**
 * enginePreProcessJson_Merge_Metadata.ts - Metadata optimization utilities for EnginePreProcessJson
 * Port of C# EnginePreProcessJson_Merge_Metadata.cs
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import { META_KEY } from './enginePreProcessJson_Merge_Constants.js';

/**
 * Metadata structure from _meta key
 */
interface HtmlJsonMeta {
    hasPlaceholders?: boolean;
    hasSlotholders?: boolean;
    hasArrayBlocks?: boolean;
    hasJsonKeys?: boolean;
}

/**
 * Attempts to return static HTML if the content has no dynamic elements.
 * This is an optimization that skips full template processing for static content.
 * Port of C# TryReturnStaticHtml method
 */
export function tryReturnStaticHtml(htmlJson: JsonObject): { success: boolean; staticHtml: string } {
    if (!htmlJson) {
        return { success: false, staticHtml: '' };
    }

    if (htmlJson.has(META_KEY)) {
        const metaValue = htmlJson.get(META_KEY);
        const metaObj = metaValue.asObject();
        if (metaObj) {
            const hasPlaceholders = metaObj.get('hasPlaceholders').asBool() === true;
            const hasSlotholders = metaObj.get('hasSlotholders').asBool() === true;
            const hasArrayBlocks = metaObj.get('hasArrayBlocks').asBool() === true;
            const hasJsonKeys = metaObj.get('hasJsonKeys').asBool() === true;

            // If no dynamic elements, convert directly to HTML
            if (!hasPlaceholders && !hasSlotholders && !hasArrayBlocks && !hasJsonKeys) {
                // Also check for conditional entries (TYPE_CONDITIONAL = 'c') directly in the htmlJson keys
                for (const key of htmlJson.keys()) {
                    if (key.length > 0 && key[0] === 'c') {
                        return { success: false, staticHtml: '' }; // Has conditional - not static
                    }
                }

                const staticHtml = GenerateHtmlJson.convertJsonToHtml(htmlJson);
                return { success: true, staticHtml };
            }
        }
    }

    return { success: false, staticHtml: '' };
}

/**
 * Gets metadata from HtmlJson structure
 */
export function getMetadata(htmlJson: JsonObject | null): HtmlJsonMeta | null {
    if (!htmlJson) {
        return null;
    }

    if (!htmlJson.has(META_KEY)) return null;
    const metaValue = htmlJson.get(META_KEY);
    const metaObj = metaValue.asObject();
    if (!metaObj) return null;
    return {
        hasPlaceholders: metaObj.get('hasPlaceholders').asBool() === true,
        hasSlotholders: metaObj.get('hasSlotholders').asBool() === true,
        hasArrayBlocks: metaObj.get('hasArrayBlocks').asBool() === true,
        hasJsonKeys: metaObj.get('hasJsonKeys').asBool() === true
    };
}

export const mergeMetadataMethods = {
    tryReturnStaticHtml(htmlJson: JsonObject): { success: boolean; staticHtml: string } {
        return tryReturnStaticHtml(htmlJson);
    }
};
